import {
  getEmptyFunnelAnswers,
  FUNNEL_SECTION_IDS,
  type FunnelAnswers,
  type Posture,
  type SectionId,
} from "./types";

export const FUNNEL_SESSION_KEY = "sill-funnel-session";

export interface FunnelSessionState {
  posture: Posture;
  sectionId: SectionId;
  answers: FunnelAnswers;
  /** Backend user id, attached once the account-step submit succeeds. */
  accountId?: string;
}

/**
 * Draft answers and position survive refresh in this tab. After account
 * creation the draft is bound to that account; profile existence alone does
 * not mean the remaining verification/financial screens were completed.
 * The terminal exit clears the draft. Server data remains authoritative for
 * bank figures, legal acceptance, and the saved profile.
 */
export function saveFunnelSession(state: FunnelSessionState): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(FUNNEL_SESSION_KEY, JSON.stringify(state));
  } catch {
    // Storage can be unavailable; a draft failure must not break the form.
  }
}

export function loadFunnelSession(): FunnelSessionState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FUNNEL_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FunnelSessionState> | null;
    if (
      !parsed ||
      (parsed.posture !== "marketplace" && parsed.posture !== "invited") ||
      typeof parsed.sectionId !== "string" ||
      !parsed.answers ||
      typeof parsed.answers !== "object" ||
      Array.isArray(parsed.answers) ||
      (parsed.accountId !== undefined &&
        (typeof parsed.accountId !== "string" || !parsed.accountId))
    )
      return null;
    const answers = { ...getEmptyFunnelAnswers(), ...parsed.answers };
    if (!FUNNEL_SECTION_IDS.includes(parsed.sectionId)) {
      return null;
    }
    return { ...parsed, answers } as FunnelSessionState;
  } catch {
    return null;
  }
}

export function loadFunnelSessionForAccount(
  accountId: string,
): FunnelSessionState | null {
  const session = loadFunnelSession();
  return session?.accountId === accountId ? session : null;
}

export function clearFunnelSession(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(FUNNEL_SESSION_KEY);
  } catch {
    // Keep terminal navigation usable when browser storage is unavailable.
  }
}
